import { logger } from "@/lib/logger";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BarChart3, Mail, ArrowLeft, CheckCircle2, Calendar, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isEmailSent, setIsEmailSent] = useState(false);
  const [demoResetUrl, setDemoResetUrl] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setIsLoading(true);
    
    try {
      const response = await fetch(`${API}/auth/forgot-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      
      setIsEmailSent(true);
      
      // In demo mode, show the reset URL for testing
      if (data.reset_url) {
        setDemoResetUrl(data.reset_url);
        toast.success("Demo mode: Reset link generated!");
      } else {
        toast.success("If an account exists, you'll receive a reset email.");
      }
    } catch (error) {
      logger.error("Forgot password error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        {/* Logo */}
        <div className="flex items-center gap-3 mb-8 justify-center">
          <div className="w-10 h-10 rounded-lg overflow-hidden flex items-center justify-center">
            <img src="/roas-ai-logo.png" alt="ROAS-AI" className="w-10 h-10 object-contain" />
          </div>
          <span className="font-heading text-xl text-white font-bold">ROAS-AI</span>
        </div>

        <div className="bg-[#121212] border border-white/10 rounded-2xl p-8">
          <AnimatePresence mode="wait">
            {!isEmailSent ? (
              <motion.div
                key="form"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              >
                <div className="text-center mb-6">
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-violet-500/10 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-violet-400" />
                  </div>
                  <h2 className="font-heading text-2xl text-white font-bold mb-2">
                    Forgot Password?
                  </h2>
                  <p className="text-zinc-400 text-sm">
                    No worries! Enter your email and we'll send you a reset link.
                  </p>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-zinc-400">Email Address</Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                      <Input
                        id="email"
                        type="email"
                        placeholder="you@company.com"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="pl-10 bg-black/50 border-white/10 text-white placeholder:text-zinc-500"
                        data-testid="forgot-email-input"
                      />
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-violet-600 hover:bg-violet-700 text-white font-semibold rounded-lg py-5"
                    data-testid="send-reset-btn"
                  >
                    {isLoading ? (
                      <div className="spinner border-white border-t-transparent" />
                    ) : (
                      "Send Reset Link"
                    )}
                  </Button>
                </form>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-center"
              >
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-emerald-400" />
                </div>
                <h2 className="font-heading text-2xl text-white font-bold mb-2">
                  Check Your Email
                </h2>
                <p className="text-zinc-400 text-sm mb-6">
                  If an account exists for <span className="text-white">{email}</span>, 
                  you'll receive a password reset link shortly.
                </p>

                {/* Demo mode: Show reset URL */}
                {demoResetUrl && (
                  <div className="mb-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
                    <p className="text-amber-400 text-xs font-semibold mb-2">
                      🔧 DEMO MODE - Reset Link:
                    </p>
                    <button
                      onClick={() => {
                        const url = new URL(demoResetUrl);
                        navigate(`/reset-password${url.search}`);
                      }}
                      className="text-amber-300 text-sm underline hover:text-amber-200 break-all"
                      data-testid="demo-reset-link"
                    >
                      Click here to reset password
                    </button>
                  </div>
                )}

                <p className="text-zinc-500 text-sm mb-4">
                  Didn't receive the email? Check your spam folder or try again.
                </p>

                <Button
                  onClick={() => {
                    setIsEmailSent(false);
                    setDemoResetUrl(null);
                    setEmail("");
                  }}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Try another email
                </Button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Back to login */}
          <div className="mt-6 pt-6 border-t border-white/5 text-center">
            <Link
              to="/login"
              className="inline-flex items-center text-zinc-400 hover:text-white text-sm transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Sign In
            </Link>
          </div>

          {/* Fast-track approval link */}
          <div className="mt-4 text-center">
            <a
              href="https://calendar.app.google/gbZSzz4TiFAYYSjL8"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-emerald-400 hover:text-emerald-300 text-sm transition-colors"
            >
              <Calendar className="w-4 h-4 mr-2" />
              Schedule a call for fast-track approval
              <ExternalLink className="w-3 h-3 ml-1" />
            </a>
          </div>
        </div>
      </motion.div>
      <p className="text-zinc-500 text-xs text-right mt-6">Copyright (c) 2026 Beruf LLC</p>
    </div>
  );
}
